import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles, Send, MessageSquare, Target, AlertTriangle, Banknote, ExternalLink, Users, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SetupChecklist from "@/components/onboarding/SetupChecklist";
import { Button } from "@/components/ui/button";
import SaveGrantButton from "@/components/opportunities/SaveGrantButton";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import CredentialsCard from "@/components/coach/CredentialsCard";
import NewForYouStrip from "@/components/coach/NewForYouStrip";
import CoachAlertPrefs from "@/components/coach/CoachAlertPrefs";
import { useAssessmentProgress, type AssessmentItemType } from "@/hooks/useAssessmentProgress";
import { hashItemKey, matchCredentialFromText } from "@/lib/credentials";
import { useCompanyCredentials } from "@/hooks/useCompanyCredentials";

type Profile = {
  id: string;
  user_id: string;
  name: string;
  stage: string | null;
  industry: string | null;
  county: string | null;
  team_size: string | null;
  revenue_range: string | null;
  goals: string | null;
  blockers: string | null;
  funding_sought: string[] | null;
};

type Msg = { role: "user" | "assistant" | "system"; content: string };

const STAGES = ["Idea", "Pre-revenue", "Early revenue", "Scaling"];
const REVENUES = ["Pre-revenue", "<$50K", "$50K–$250K", "$250K–$1M", "$1M+"];
const TEAMS = ["Just me", "2–5", "6–20", "20+"];
const FUNDING_OPTIONS = ["Grants", "Loans", "Equity", "Contracts/RFPs", "Sponsorships"];

const Coach = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  const { data: profile, isLoading: profileLoading } = useQuery({
    enabled: !!user,
    queryKey: ["company-profile", user?.id],
    queryFn: async (): Promise<Profile | null> => {
      const { data } = await supabase.from("company_profiles").select("*").eq("user_id", user!.id).maybeSingle();
      return (data as Profile) ?? null;
    },
  });

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="container mx-auto px-6 py-32 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="container mx-auto px-6 pt-32 pb-16">
        <SetupChecklist variant="inline" className="max-w-3xl mb-6" />
        <div className="max-w-3xl mb-8">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-primary mb-3">
            <Sparkles className="h-3.5 w-3.5" /> AI Enhanced Strategic Assessment
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-3">Position your business to win.</h1>
          <p className="text-muted-foreground text-lg">Tell the coach what you're building. Get concrete next steps, gaps to close, and resources matched to your goals — grants, legislation, partners, and insights.</p>
        </div>

        {profile ? <CoachWorkspace profile={profile} /> : <IntakeWizard onCreated={() => queryClient.invalidateQueries({ queryKey: ["company-profile", user?.id] })} />}
      </main>
      <SiteFooter />
    </div>
  );
};

const IntakeWizard = ({ onCreated }: { onCreated: () => void }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "", stage: "", industry: "", county: "", team_size: "", revenue_range: "",
    goals: "", blockers: "", funding_sought: [] as string[],
  });
  const [saving, setSaving] = useState(false);

  const toggleFunding = (v: string) => {
    setForm((f) => ({ ...f, funding_sought: f.funding_sought.includes(v) ? f.funding_sought.filter((x) => x !== v) : [...f.funding_sought, v] }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.goals.trim()) {
      toast.error("Company name and goals are required.");
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("company_profiles").insert({ ...form, user_id: user!.id });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Profile created. Generating your assessment…");
    onCreated();
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border rounded-xl p-6 md:p-8 space-y-6 max-w-3xl">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Company name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={200} />
        </div>
        <div>
          <Label>Industry / focus area</Label>
          <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="e.g. Health & Wellness, Construction" maxLength={200} />
        </div>
        <div>
          <Label>Stage</Label>
          <select className="w-full h-10 rounded-md bg-background border border-border px-3 text-sm" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}>
            <option value="">Select…</option>
            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label>Alabama county</Label>
          <Input value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} placeholder="e.g. Mobile" maxLength={100} />
        </div>
        <div>
          <Label>Team size</Label>
          <select className="w-full h-10 rounded-md bg-background border border-border px-3 text-sm" value={form.team_size} onChange={(e) => setForm({ ...form, team_size: e.target.value })}>
            <option value="">Select…</option>
            {TEAMS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label>Revenue range</Label>
          <select className="w-full h-10 rounded-md bg-background border border-border px-3 text-sm" value={form.revenue_range} onChange={(e) => setForm({ ...form, revenue_range: e.target.value })}>
            <option value="">Select…</option>
            {REVENUES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div>
        <Label>What are you trying to accomplish in the next 6–12 months? *</Label>
        <Textarea rows={4} maxLength={2000} value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} required />
      </div>
      <div>
        <Label>Biggest blockers</Label>
        <Textarea rows={3} maxLength={2000} value={form.blockers} onChange={(e) => setForm({ ...form, blockers: e.target.value })} />
      </div>

      <div>
        <Label className="mb-2 block">Funding you're seeking</Label>
        <div className="flex flex-wrap gap-2">
          {FUNDING_OPTIONS.map((opt) => (
            <button type="button" key={opt} onClick={() => toggleFunding(opt)}
              className={`px-3 py-1.5 rounded-full text-xs border transition ${form.funding_sought.includes(opt) ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:text-foreground"}`}>
              {opt}
            </button>
          ))}
        </div>
      </div>

      <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-semibold">
        {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…</> : "Create profile & assess"}
      </Button>
    </form>
  );
};

const CoachWorkspace = ({ profile }: { profile: Profile }) => {
  const queryClient = useQueryClient();
  const progress = useAssessmentProgress(profile.id);
  const credentials = useCompanyCredentials(profile.id);

  const { data: assessment, isLoading: aLoading } = useQuery({
    queryKey: ["company-assessment", profile.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("company_assessments").select("*")
        .eq("company_profile_id", profile.id)
        .order("created_at", { ascending: false }).limit(1).maybeSingle();
      return data;
    },
  });

  const { data: matches = [] } = useQuery({
    enabled: !!assessment?.id,
    queryKey: ["company-matches", assessment?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("company_resource_matches").select("*")
        .eq("assessment_id", assessment!.id).order("score", { ascending: false });
      return data ?? [];
    },
  });

  const runAssess = useMutation({
    mutationFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/company-coach-assess`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.session?.access_token}` },
        body: JSON.stringify({ company_profile_id: profile.id }),
      });
      if (!resp.ok) {
        if (resp.status === 402) throw new Error("Workspace AI credits exhausted.");
        if (resp.status === 429) throw new Error("AI is busy. Try again in a moment.");
        throw new Error("Assessment failed");
      }
      return resp.json();
    },
    onSuccess: () => {
      toast.success("Assessment ready");
      queryClient.invalidateQueries({ queryKey: ["company-assessment", profile.id] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  useEffect(() => {
    if (!aLoading && !assessment && !runAssess.isPending) runAssess.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aLoading, assessment]);

  const groupedMatches = (type: string) => matches.filter((m: any) => m.resource_type === type);

  // Compute "last assessed" + freshness nudge
  const assessmentAgeDays = assessment?.created_at
    ? Math.floor((Date.now() - new Date(assessment.created_at).getTime()) / 86400000)
    : null;
  const totalItems =
    ((assessment?.recommended_actions as any[])?.length ?? 0) +
    ((assessment?.gaps as any[])?.length ?? 0);
  const doneRatio = totalItems > 0 ? progress.items.length / totalItems : 0;
  const shouldRefresh =
    (assessmentAgeDays !== null && assessmentAgeDays > 30) || doneRatio >= 0.6;
  const lastAssessedLabel =
    assessmentAgeDays === null
      ? null
      : assessmentAgeDays === 0
        ? "Assessed today"
        : assessmentAgeDays === 1
          ? "Assessed yesterday"
          : assessmentAgeDays < 30
            ? `Assessed ${assessmentAgeDays} days ago`
            : `Assessed ${Math.floor(assessmentAgeDays / 30)} mo ago`;

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-6">
      <div className="space-y-6 min-w-0">
        <NewForYouStrip />
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <div className="text-xs uppercase text-muted-foreground tracking-wide">Company</div>
              <h2 className="font-display text-2xl font-semibold">{profile.name}</h2>
              <div className="text-sm text-muted-foreground mt-1">{[profile.stage, profile.industry, profile.county].filter(Boolean).join(" · ")}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                size="sm"
                variant={shouldRefresh ? "default" : "outline"}
                onClick={() => runAssess.mutate()}
                disabled={runAssess.isPending}
              >
                {runAssess.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : shouldRefresh ? (
                  <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Refresh assessment</>
                ) : (
                  <><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Re-run assessment</>
                )}
              </Button>
              {lastAssessedLabel && (
                <span className="text-[10px] text-muted-foreground">
                  {lastAssessedLabel}
                  {doneRatio > 0 && ` · ${Math.round(doneRatio * 100)}% complete`}
                </span>
              )}
            </div>
          </div>
        </div>

        {!assessment && (runAssess.isPending || aLoading) ? (
          <div className="bg-card border border-border rounded-xl p-12 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Generating your readiness assessment…
          </div>
        ) : assessment ? (
          <>
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-display text-lg font-semibold mb-2">Positioning summary</h3>
              <p className="text-sm text-foreground/90 leading-relaxed">{assessment.summary}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <ProgressList
                kind="action"
                title="Recommended actions"
                icon={<Target className="h-4 w-4 text-primary" />}
                items={(assessment.recommended_actions as any[]).map((a) => ({
                  title: a.title,
                  meta: a.priority,
                  body: a.rationale,
                }))}
                progress={progress}
                credentials={credentials}
              />
              <ProgressList
                kind="gap"
                title="Gaps to close"
                icon={<AlertTriangle className="h-4 w-4 text-primary" />}
                items={(assessment.gaps as any[]).map((g) => ({
                  title: g.area,
                  body: g.description,
                  capitalizeTitle: true,
                }))}
                progress={progress}
                credentials={credentials}
              />
            </div>

            <CredentialsCard companyProfileId={profile.id} />

            <CoachAlertPrefs />

            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3"><Banknote className="h-4 w-4 text-primary" /><h3 className="font-semibold">Funding pathways</h3></div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(assessment.funding_pathways as any[]).map((p, i) => (
                  <div key={i} className="border border-border rounded-lg p-3 text-sm">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs uppercase text-primary mt-0.5">{p.fit} fit</div>
                    <div className="text-xs text-muted-foreground mt-1">{p.why}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-display text-lg font-semibold mb-3">Matched resources</h3>
              <Tabs defaultValue="grant">
                <div className="-mx-2 overflow-x-auto">
                  <TabsList className="w-max px-2">
                    <TabsTrigger value="grant">Funding</TabsTrigger>
                    <TabsTrigger value="person">People</TabsTrigger>
                    <TabsTrigger value="legislation">Legislation</TabsTrigger>
                    <TabsTrigger value="partner">Partners</TabsTrigger>
                    <TabsTrigger value="article">Insights</TabsTrigger>
                  </TabsList>
                </div>
                {(["grant", "person", "legislation", "partner", "article"] as const).map((t) => (
                  <TabsContent key={t} value={t} className="mt-4 space-y-2">
                    {t === "grant" && groupedMatches(t).length > 0 && (
                      <p className="text-xs text-muted-foreground mb-1">
                        Saved grants live in your{" "}
                        <Link to="/profile?tab=saved" className="text-primary hover:underline">
                          profile
                        </Link>
                        .
                      </p>
                    )}
                    {groupedMatches(t).length === 0 ? (
                      <div className="text-sm text-muted-foreground">
                        {t === "person" ? "No member matches yet — invite collaborators to join." : "No matches yet."}
                      </div>
                    ) : t === "grant" ? groupedMatches(t).map((m: any) => (
                      <div key={m.id} className="block border border-border rounded-lg p-3">
                        <div className="min-w-0">
                          <div className="font-medium text-sm">{m.title}</div>
                          <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{m.description}</div>
                          {m.reason && <div className="text-xs text-primary mt-1">{m.reason}</div>}
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-border flex-wrap">
                          {m.url ? (
                            <a
                              href={m.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              Open grant <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : <span />}
                          <SaveGrantButton
                            grant={{
                              title: m.title,
                              source: null,
                              source_type: null,
                              amount: null,
                              deadline: null,
                              url: m.url ?? null,
                              description: m.description ?? null,
                              matched_area: null,
                            }}
                          />
                        </div>
                      </div>
                    )) : groupedMatches(t).map((m: any) => {
                      const href = t === "person"
                        ? `/profile/${m.resource_ref}`
                        : t === "article"
                          ? `/insights/${m.resource_ref}`
                          : (m.url ?? "#");
                      return (
                        <a key={m.id} href={href}
                          target={m.url?.startsWith("http") ? "_blank" : undefined}
                          rel={m.url?.startsWith("http") ? "noopener noreferrer" : undefined}
                          className="block border border-border rounded-lg p-3 hover:border-primary/50 transition">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex items-start gap-3">
                              {t === "person" && <Users className="h-4 w-4 text-primary mt-0.5 shrink-0" />}
                              <div className="min-w-0">
                                <div className="font-medium text-sm truncate">{m.title}</div>
                                <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{m.description}</div>
                                <div className="text-xs text-primary mt-1">{m.reason}</div>
                              </div>
                            </div>
                            {m.url && <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />}
                          </div>
                        </a>
                      );
                    })}
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </>
        ) : null}
      </div>

      <div className="lg:sticky lg:top-28 self-start min-w-0">
        <CompanyCoachChat profileId={profile.id} />
      </div>
    </div>
  );
};

type ProgressListItem = {
  title: string;
  body?: string;
  meta?: string;
  capitalizeTitle?: boolean;
};

type ProgressListProps = {
  kind: AssessmentItemType;
  title: string;
  icon: React.ReactNode;
  items: ProgressListItem[];
  progress: ReturnType<typeof useAssessmentProgress>;
  credentials: ReturnType<typeof useCompanyCredentials>;
};

const ProgressItemRow = ({
  item,
  kind,
  progress,
  credentials,
}: {
  item: ProgressListItem;
  kind: AssessmentItemType;
  progress: ReturnType<typeof useAssessmentProgress>;
  credentials: ReturnType<typeof useCompanyCredentials>;
}) => {
  const [keyHash, setKeyHash] = useState<string | null>(null);
  useEffect(() => {
    hashItemKey(kind, item.title).then(setKeyHash);
  }, [kind, item.title]);
  const done = keyHash ? progress.isDone(kind, keyHash) : false;

  const onToggle = async () => {
    const result = await progress.toggleDone(kind, item.title);
    if (result?.action === "added") {
      const cred = matchCredentialFromText(item.title);
      if (cred && credentials.byKey.get(cred.key)?.status !== "earned") {
        toast.success(`Marked done — claim the ${cred.label} badge?`, {
          action: {
            label: "Claim",
            onClick: () => credentials.setStatus(cred.key, "earned"),
          },
        });
      } else {
        toast.success("Marked done");
      }
    }
  };

  return (
    <li className={`border-l-2 pl-3 ${done ? "border-primary/60" : "border-primary/40"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className={`min-w-0 flex-1 ${done ? "opacity-60" : ""}`}>
          <div className={`font-medium ${item.capitalizeTitle ? "capitalize" : ""} ${done ? "line-through" : ""}`}>
            {item.title}
            {item.meta && (
              <span className="text-xs uppercase text-muted-foreground ml-1">{item.meta}</span>
            )}
          </div>
          {item.body && <div className="text-muted-foreground text-xs mt-1">{item.body}</div>}
        </div>
        <Button
          type="button"
          variant={done ? "secondary" : "outline"}
          size="sm"
          className="h-7 px-2 text-[11px] shrink-0"
          onClick={onToggle}
        >
          {done ? (
            <><Check className="h-3 w-3 mr-1 text-primary" /> Done</>
          ) : (
            "Mark done"
          )}
        </Button>
      </div>
    </li>
  );
};

const ProgressList = ({ kind, title, icon, items, progress, credentials }: ProgressListProps) => {
  const [showResolved, setShowResolved] = useState(false);
  const [resolvedKeys, setResolvedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const set = new Set<string>();
      for (const it of items) {
        const k = await hashItemKey(kind, it.title);
        if (progress.isDone(kind, k)) set.add(it.title);
      }
      if (!cancelled) setResolvedKeys(set);
    })();
    return () => {
      cancelled = true;
    };
  }, [items, progress, kind]);

  const visible = items.filter((i) => showResolved || !resolvedKeys.has(i.title));
  const resolvedCount = resolvedKeys.size;

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold">{title}</h3>
        </div>
        {resolvedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowResolved((s) => !s)}
            className="text-[11px] text-muted-foreground hover:text-primary transition"
          >
            {showResolved ? "Hide" : "Show"} resolved ({resolvedCount})
          </button>
        )}
      </div>
      {visible.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">
          {resolvedCount > 0 ? "All clear — every item resolved." : "Nothing here yet."}
        </div>
      ) : (
        <ol className="space-y-3 text-sm">
          {visible.map((it, i) => (
            <ProgressItemRow
              key={`${it.title}-${i}`}
              item={it}
              kind={kind}
              progress={progress}
              credentials={credentials}
            />
          ))}
        </ol>
      )}
    </div>
  );
};

const CompanyCoachChat = ({ profileId }: { profileId: string }) => {
  const queryClient = useQueryClient();
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamMsgs, setStreamMsgs] = useState<Msg[] | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: history = [], isLoading } = useQuery({
    queryKey: ["company-coach", profileId],
    queryFn: async (): Promise<Msg[]> => {
      const { data } = await supabase.from("company_coach_messages")
        .select("role, content").eq("company_profile_id", profileId).order("created_at", { ascending: true });
      return (data ?? []) as Msg[];
    },
  });

  const messages = streamMsgs ?? history;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput(""); setStreaming(true);
    const optimistic: Msg[] = [...history, { role: "user", content: text }, { role: "assistant", content: "" }];
    setStreamMsgs(optimistic);

    try {
      const { data: session } = await supabase.auth.getSession();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/company-coach-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.session?.access_token}` },
        body: JSON.stringify({ company_profile_id: profileId, user_message: text }),
      });
      if (resp.status === 429) { toast.error("AI is busy."); throw new Error("rate"); }
      if (resp.status === 402) { toast.error("AI credits exhausted."); throw new Error("credits"); }
      if (!resp.ok || !resp.body) throw new Error("Stream failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = ""; let assistantText = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, nl);
          buffer = buffer.slice(nl + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") continue;
          try {
            const parsed = JSON.parse(json);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              assistantText += delta;
              setStreamMsgs((cur) => {
                if (!cur) return cur;
                const next = [...cur];
                next[next.length - 1] = { role: "assistant", content: assistantText };
                return next;
              });
            }
          } catch { /* partial */ }
        }
      }
    } catch (e: any) {
      if (e?.message !== "rate" && e?.message !== "credits") toast.error("Coach failed to respond.");
    } finally {
      setStreaming(false); setStreamMsgs(null);
      queryClient.invalidateQueries({ queryKey: ["company-coach", profileId] });
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl flex flex-col h-[640px]">
      <div className="flex items-center gap-2 border-b border-border px-5 py-3">
        <MessageSquare className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-sm">AI Enhanced Strategic Assessment</h3>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? <div className="text-sm text-muted-foreground">Loading…</div> :
          messages.length === 0 ? (
            <div className="text-sm text-muted-foreground">Ask anything about growing your business. Try: <em>"What credentials should I pursue first?"</em></div>
          ) : messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[88%] rounded-lg px-3 py-2 text-sm ${m.role === "user" ? "bg-primary/15 border border-primary/30" : "bg-secondary/40 border border-border"}`}>
                {m.role === "assistant" ? (
                  <div className="prose prose-sm prose-invert max-w-none [&_p]:my-1 [&_ul]:my-1">
                    {m.content ? <ReactMarkdown>{m.content}</ReactMarkdown> : <Loader2 className="h-4 w-4 animate-spin" />}
                  </div>
                ) : <p className="whitespace-pre-wrap">{m.content}</p>}
              </div>
            </div>
          ))}
      </div>
      <div className="border-t border-border p-2 flex gap-2">
        <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} className="resize-none" placeholder="Ask the coach…" disabled={streaming}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button onClick={send} disabled={!input.trim() || streaming} className="self-end">
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

export default Coach;